import { UsageWithPercentage } from '@/types/subscription';
import { useLanguage } from '@/contexts';
import { FeatureKey } from '@/types/subscription';

interface FooterUsageBarProps {
  usage: UsageWithPercentage[];
}

/**
 * FooterUsageBar
 * Displays top 3 usage features in a horizontal bar at the bottom of the page
 */
export function FooterUsageBar({ usage }: FooterUsageBarProps) {
  const { t } = useLanguage();

  // Take only top 3
  const topThree = usage.slice(0, 3);

  if (topThree.length === 0) {
    return null;
  }

  const getColorClasses = (percentage: number) => {
    if (percentage >= 90) {
      return {
        bg: 'bg-red-500 dark:bg-red-400',
        text: 'text-red-700 dark:text-red-300',
        lightBg: 'bg-red-50 dark:bg-red-900/30'
      };
    }
    if (percentage >= 75) {
      return {
        bg: 'bg-yellow-500 dark:bg-yellow-400',
        text: 'text-yellow-700 dark:text-yellow-300',
        lightBg: 'bg-yellow-50 dark:bg-yellow-900/30'
      };
    }
    return {
      bg: 'bg-green-500 dark:bg-green-400',
      text: 'text-green-700 dark:text-green-300',
      lightBg: 'bg-green-50 dark:bg-green-900/30'
    };
  };

  const formatFeatureKey = (key: FeatureKey): string => {
    return t(`usageWidget.features.${key}`);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 shadow-lg dark:shadow-2xl dark:shadow-slate-900/50 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Title */}
          <div className="flex items-center gap-2 min-w-fit">
            <span className="text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide transition-colors duration-300">
              {t('usageWidget.title')}
            </span>
          </div>

          {/* Usage items */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
            {topThree.map((item) => {
              const colors = getColorClasses(item.percentage_used || 0);
              return (
                <div
                  key={item.feature_key}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border ${colors.lightBg} border-gray-200 dark:border-slate-600 transition-colors duration-300`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`w-1.5 h-1.5 rounded-full ${colors.bg}`}></div>
                    <span className="text-xs font-medium text-gray-700 dark:text-slate-200 truncate transition-colors duration-300">
                      {formatFeatureKey(item.feature_key)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className={`text-xs font-bold ${colors.text} transition-colors duration-300`}>
                      {t('usageWidget.status.limited')
                        .replace('{{current}}', String(item.current_usage))
                        .replace('{{limit}}', String(item.limit_value))}
                    </span>
                    <span className={`text-xs font-semibold ${colors.text} transition-colors duration-300`}>
                      {t('usageWidget.status.percentage').replace('{{percentage}}', String(item.percentage_used))}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

